import { api } from '../../../../components/common';
import { 
  Address,
  CreateAddressRequest,
  UpdateAddressRequest,
  AddressSearchParams
} from '../types';
import { PaginatedResponse } from '../../../../types';

// Helper function to convert server verified (number 0/1) to client boolean
const convertVerifiedToBoolean = (verified: any): boolean => {
  if (verified === undefined || verified === null) return false;
  // Handle number: 1 = true, 0 = false
  if (typeof verified === 'number') {
    return verified === 1;
  }
  // Handle string: '1' = true, '0' = false
  if (typeof verified === 'string') {
    return verified === '1';
  }
  // Handle boolean
  return verified === true;
};

// Convert server Address to client Address (convert verified from number to boolean, keep inUseCount as number)
const convertAddressFromApi = (serverAddress: any): Address => {
  const converted: Address = {
    ...serverAddress,
    verified: convertVerifiedToBoolean(serverAddress.verified),
    inUseCount: serverAddress.inUseCount !== undefined ? Number(serverAddress.inUseCount) : undefined
  };
  return converted;
};

// Convert array of addresses
const convertAddressesFromApi = (addresses: any[]): Address[] => {
  return addresses.map(convertAddressFromApi);
};

export const addressesApi = {
  getAll: (params: AddressSearchParams = {}): Promise<PaginatedResponse<Address>> => {
    // Build query params manually using URLSearchParams to properly serialize sorts array
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    // Handle multiple sorts - encode as JSON string
    if (params.sorts && params.sorts.length > 0) {
      queryParams.append('sorts', JSON.stringify(params.sorts));
    }
    
    // Handle multiple search fields - encode as JSON string
    if (params.fields && params.fields.length > 0) {
      queryParams.append('fields', JSON.stringify(params.fields));
    }
    if (params.availableForProperty) {
      queryParams.append('availableForProperty', 'true');
    }
    if (params.excludePropertyId != null) {
      queryParams.append('excludePropertyId', params.excludePropertyId.toString());
    }
    
    return api.get(`/addresses?${queryParams.toString()}`).then(res => {
      const response = res.data;
      return {
        ...response,
        data: convertAddressesFromApi(response.data || [])
      };
    });
  },
    
  getById: (id: number): Promise<Address> =>
    api.get(`/addresses/${id}`).then(res => convertAddressFromApi(res.data)),
    
  create: (address: CreateAddressRequest): Promise<Address> =>
    api.post('/addresses', address).then(res => convertAddressFromApi(res.data)),
    
  update: (id: number, address: UpdateAddressRequest): Promise<Address> =>
    api.put(`/addresses/${id}`, address).then(res => convertAddressFromApi(res.data)),
    
  delete: (id: number): Promise<void> =>
    api.delete(`/addresses/${id}`).then(() => {}),
};

